import { getStyles, getQuickProductInfo } from '../common/callAPIs';
import { useEffect, useState } from 'react';
import { replaceS3WithCF } from '../common/replaceS3WithCF';
import { styleHandling } from '../js/styleHandling';
import { object } from '../js/webGL';

export function useLoadStyle(queryInfo, productInfo) {
  const [styleList, setStyleList] = useState([]);
  const [defaultStyleLoaded, setDefaultStyleLoaded] = useState(false);

  useEffect(() => {
    loadStyles();
  }, [productInfo]);

  // Simple immediate image preloading function
  const preloadStyleImages = (styles) => {
    if (!styles || styles.length === 0) return;

    // Start preloading immediately - browser handles queuing automatically
    styles.forEach((style) => {
      if (style.Thumbnail) {
        const thumbImg = new Image();
        thumbImg.src = style.Thumbnail;
      }
      if (style.main_texture) {
        const textureImg = new Image();
        textureImg.src = style.main_texture;
      }
    });
  };

  // Centralized style application with duplicate prevention
  const applyStyleSafely = (style) => {
    if (!style || !style.Id || !style.main_texture) return false;

    // Check if this style is already applied
    if (window.currentAppliedStyleId === style.Id) {
      return false;
    }

    // Apply the style
    if (styleHandling) {
      styleHandling.applyStyleToAllSections(style);
      window.currentAppliedStyleId = style.Id;
      return true;
    }

    return false;
  };

  // Apply default style when styleList is loaded and 3D object is ready
  useEffect(() => {
    if (styleList && styleList.length > 0) {
      window.styleList = styleList;

      // Start preloading images immediately
      preloadStyleImages(styleList);

      const defaultStyle = styleList[0];
      if (defaultStyle && defaultStyle.main_texture) {
        setDefaultStyleLoaded(true);
        if (object) {
          applyStyleSafely(defaultStyle);
        }
      }
    } else if (styleList && styleList.length === 0) {
      // No styles available, mark as loaded to stop infinite loading
      setDefaultStyleLoaded(true);
    }
  }, [styleList]);

  const loadStyles = async () => {
    try {
      const quickRes = await getQuickProductInfo(queryInfo, productInfo);
      const quickData = await quickRes.json();

      if (!quickData || !quickData.product_id) {
        setStyleList([]);
        setDefaultStyleLoaded(true);
        return;
      }

      const productInfoWithId = {
        ...productInfo,
        product_id: quickData.product_id,
      };

      getStyles(queryInfo, productInfoWithId)
        .then((res) => {
          if (res.status !== 200) throw new Error('Fail to load styles');
          return res.json();
        })
        .then((data) => {
          if (!data) throw new Error('Something went wrong');

          // Handle the products API response structure
          const items = data?.items || [];
          const productData = items[0];

          if (!productData) {
            setStyleList([]);
            setDefaultStyleLoaded(true);
            return;
          }

          if (!productData.options) {
            setStyleList([]);
            setDefaultStyleLoaded(true);
            return;
          }

          // Extract styles from all options (original safe logic)
          const allStyles = [];
          let defaultStyle = null;

          productData.options.forEach((option, optionIndex) => {
            if (!option.values) return;

            // Extract styles from values (pairs: texture, preview)
            option.values.forEach((_, index) => {
              if (index % 2 === 0) {
                const textureItem = option.values[index];
                const previewItem = option.values[index + 1];

                if (textureItem?.title && previewItem?.title) {
                  const style = {
                    Id: `${productInfoWithId.product_id}_${optionIndex}_${
                      Math.floor(index / 2) + 1
                    }`,
                    Thumbnail: replaceS3WithCF(previewItem.title),
                    main_texture: replaceS3WithCF(textureItem.title),
                  };

                  // Check if this is the default style
                  if (option.title === 'default') {
                    defaultStyle = style;
                  } else {
                    allStyles.push(style);
                  }
                }
              }
            });
          });

          // Place default style at index 0 if found, then add other styles
          const finalStyles = defaultStyle ? [defaultStyle, ...allStyles] : allStyles;

          setStyleList(finalStyles);

          // If no styles are loaded, still mark as ready to avoid blocking product loading
          if (finalStyles.length === 0) {
            setDefaultStyleLoaded(true);
          }
        })
        .catch((error) => {
          console.log('ERROR WHEN LOADING STYLES', error);
          setStyleList([]);
          setDefaultStyleLoaded(true);
        });
    } catch (error) {
      console.error('Error in loadStyles:', error);
      setStyleList([]);
      setDefaultStyleLoaded(true);
    }
  };

  return { styleList, defaultStyleLoaded };
}
